import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Shield, Check, X, Loader2 } from "lucide-react";

interface AuthorizationDetails {
  authorization_id: string;
  client: {
    name: string;
    client_id: string;
  };
  redirect_uri: string;
  scope?: string;
}

export default function OAuthConsent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authorizationId = searchParams.get("authorization_id");

  const [authDetails, setAuthDetails] = useState<AuthorizationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadAuthDetails() {
      if (!authorizationId) {
        setError("Missing authorization_id");
        setLoading(false);
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        navigate(
          `/login?redirect=/oauth/consent?authorization_id=${encodeURIComponent(authorizationId)}`
        );
        return;
      }

      const { data, error: fetchError } =
        await (supabase.auth as any).oauth.getAuthorizationDetails(authorizationId);

      if (fetchError) {
        setError(fetchError.message);
        setLoading(false);
        return;
      }

      // If previously consented, redirect immediately
      if (data && !("authorization_id" in data)) {
        window.location.href = data.redirect_url;
        return;
      }

      setAuthDetails(data);
      setLoading(false);
    }

    loadAuthDetails();
  }, [authorizationId, navigate]);

  async function handleApprove() {
    if (!authorizationId) return;
    setSubmitting(true);

    const { data, error: approveError } =
      await (supabase.auth as any).oauth.approveAuthorization(authorizationId);

    if (approveError) {
      setError(approveError.message);
      setSubmitting(false);
    } else {
      // Redirect to the MCP client's callback
      window.location.href = data.redirect_url;
      // If the redirect doesn't navigate away (e.g. localhost callback already closed),
      // show a success message after a brief delay
      setTimeout(() => {
        setSubmitting(false);
        setAuthDetails(null);
        setError(null);
      }, 3000);
    }
  }

  async function handleDeny() {
    if (!authorizationId) return;
    setSubmitting(true);

    const { data, error: denyError } =
      await (supabase.auth as any).oauth.denyAuthorization(authorizationId);

    if (denyError) {
      setError(denyError.message);
      setSubmitting(false);
    } else {
      window.location.href = data.redirect_url;
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!authDetails) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-2">
            <Check className="h-8 w-8 text-success mx-auto" />
            <p className="text-foreground font-medium">Authorization complete</p>
            <p className="text-muted-foreground text-sm">You can close this tab and return to your AI tool.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const scopes = authDetails.scope?.trim().split(" ").filter(Boolean) ?? [];

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Authorize {authDetails.client.name}</CardTitle>
          <CardDescription>
            This application wants to access your Cosmo account.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {scopes.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                Requested permissions:
              </p>
              <ul className="space-y-1">
                {scopes.map((scope) => (
                  <li
                    key={scope}
                    className="flex items-center gap-2 text-sm text-foreground"
                  >
                    <Check className="h-4 w-4 text-success" />
                    {scope}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleDeny}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <X className="h-4 w-4 mr-1" />
                  Deny
                </>
              )}
            </Button>
            <Button
              className="flex-1"
              onClick={handleApprove}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            By approving, {authDetails.client.name} will be able to access your
            Cosmo account on your behalf.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
